/**
 * Voice Mode Service - OpenAI Realtime Voice AI Integration
 * 
 * Manages session lifecycle and credit tracking for voice conversations.
 * Uses our backend to create OpenAI Realtime sessions, then connects via
 * WebRTC on web, iOS, and Android.
 * 
 * CRITICAL: This service must send heartbeats every 30 seconds for credit deduction.
 * Without heartbeats, no credits are deducted during the session.
 */

import { Platform } from 'react-native';
import { voiceApi } from './api';
import { API_HOST, SOCKET_HOST } from '@/constants/config';
import { useVoiceModeStore, useAuthStore, useModalStore, VOICE_MODE_STATES } from '@/store';
import { devLog, devWarn } from '@/lib/devLog';
import voiceSocketManager from '@/lib/voiceSocket';

// Heartbeat interval for credit tracking - MUST be 30 seconds
export const HEARTBEAT_INTERVAL_MS = 30000;

/**
 * Voice Mode Service
 * Manages session lifecycle, credit tracking, and configuration.
 * Audio handling is done by the VoiceSocket service.
 */
class VoiceModeService {
  constructor() {
    // Session state
    this.sessionId = null;
    this.heartbeatInterval = null;
    this.firstHeartbeatTimeout = null;
    this.sessionStartTime = null;
    this.heartbeatCount = 0;
    
    // Session configuration (from backend)
    this.voiceId = null;
    this.provider = null;
    this.audioConfig = null;
    this.vadConfig = null;
    
    // Activity-based credit tracking. Tracked in milliseconds — flooring to
    // whole seconds on every 30s tick discarded the sub-second remainder
    // each time, drifting the billable duration low over long sessions.
    this.activeDurationMs = 0;
    this.totalDurationMs = 0;
    this.isActive = true;
    this.lastHeartbeatTime = null;

    // Consecutive heartbeat failures. If the backend session is gone (404)
    // or unreachable for several intervals, we end the call instead of
    // letting the user keep talking on an unbilled OpenAI connection.
    this.heartbeatFailureCount = 0;
  }

  /**
   * Get current total session duration in seconds
   * CRITICAL: This must return a positive number for heartbeats to work
   */
  getDurationSeconds() {
    if (!this.sessionStartTime) return 0;
    return Math.floor((Date.now() - this.sessionStartTime) / 1000);
  }

  /**
   * Get the active duration (only time when isActive)
   */
  getActiveDurationSeconds() {
    return Math.floor(this.activeDurationMs / 1000);
  }

  /**
   * Get the total session duration
   */
  getTotalDurationSeconds() {
    return Math.floor(this.totalDurationMs / 1000);
  }

  /**
   * Get the current voice ID
   */
  getVoiceId() {
    return this.voiceId;
  }

  /**
   * Get audio configuration from backend
   */
  getAudioConfig() {
    return this.audioConfig;
  }

  /**
   * Get VAD configuration from backend
   */
  getVadConfig() {
    return this.vadConfig;
  }

  /**
   * Start a voice session with the backend.
   * Returns session info used for Realtime WebRTC SDP exchange.
   */
  async startSession() {
    const { setState, setError, setSession, setConversationId } = useVoiceModeStore.getState().actions;
    const { conversationId: existingConversationId, selectedVoice, voices } = useVoiceModeStore.getState();
    
    devLog('[Voice] startSession called with store conversationId:', existingConversationId);
    
    try {
      // NOTE: no separate /status pre-check here. POST /session performs the
      // exact same credit/availability validation server-side and returns the
      // same error codes — the extra round trip (with its credit aggregation)
      // only added startup latency.

      // ── All platforms: Use REST session creation ──
      // Web, iOS, and Android use the REST-created session for OpenAI
      // Realtime WebRTC SDP exchange. The old native raw WebSocket /
      // Socket.IO flows are kept as fallback code paths but are no longer
      // the default connection route.
      let sessionResponse;
      try {
        const isVoiceValid = Array.isArray(voices) && voices.some(v => v?.id === selectedVoice);
        const voiceIdToUse = isVoiceValid ? selectedVoice : 'marin';
        devLog('[Voice] Calling voiceApi.startSession with:', {
          voiceId: voiceIdToUse || null,
          conversationId: existingConversationId || '(null - will create new)',
          apiHost: API_HOST,
          socketHost: SOCKET_HOST,
        });
        sessionResponse = await voiceApi.startSession(voiceIdToUse || null, existingConversationId || null);
      } catch (sessionError) {
        console.error('[Voice] Session start failed:', sessionError);
        // Preserve the structured error code (e.g. INSUFFICIENT_CREDITS) so
        // the overlay can classify the error the same way the old /status
        // pre-check did.
        const wrapped = new Error(sessionError.message || 'Session start failed');
        if (sessionError?.data?.code) {
          wrapped.reason = sessionError.data.code;
        }
        throw wrapped;
      }
      
      if (!sessionResponse.success) {
        throw new Error(sessionResponse.message || 'Backend returned success=false');
      }

      // CRITICAL: Save session ID and start time for heartbeat/end calls
      this.sessionId = sessionResponse.session?.id;
      this.sessionStartTime = Date.now();
      
      // Store new configuration from backend
      this.voiceId = sessionResponse.voiceId || 'marin';
      this.provider = sessionResponse.provider || 'openai-realtime';
      this.audioConfig = sessionResponse.config?.audio || {
        inputSampleRate: 16000,
        outputSampleRate: 24000
      };
      this.vadConfig = sessionResponse.config?.vad || {
        silenceThresholdMs: 500,
        volumeThreshold: 30,
        debounceMs: 100
      };
      
      devLog('[Voice] Session response:', {
        sessionId: this.sessionId,
        conversationId: sessionResponse.conversationId,
        voiceId: this.voiceId,
        provider: this.provider,
        audioConfig: this.audioConfig,
        vadConfig: this.vadConfig,
      });
      
      // Initialize activity tracking state
      this.activeDurationMs = 0;
      this.totalDurationMs = 0;
      this.isActive = true;
      this.lastHeartbeatTime = Date.now();
      this.heartbeatFailureCount = 0;

      // Store the returned conversationId (may be existing or newly created)
      if (sessionResponse.conversationId) {
        devLog('[Voice] Setting conversationId in store:', sessionResponse.conversationId);
        setConversationId(sessionResponse.conversationId);
      } else {
        devWarn('[Voice] No conversationId returned from session start');
      }

      // Update store with session data
      if (sessionResponse.session && sessionResponse.config) {
        setSession(sessionResponse.session, sessionResponse.config, sessionResponse.credits);
      }

      // CRITICAL: Start heartbeat interval for credit deduction
      this.startHeartbeat();

      return sessionResponse;
    } catch (error) {
      console.error('[Voice] Voice session error:', error);
      setError(error.message || 'Failed to connect to voice service');
      setState(VOICE_MODE_STATES.ERROR);
      throw error;
    }
  }

  /**
   * Start heartbeat for credit deduction
   * CRITICAL: This is where credits are actually deducted!
   * Heartbeats must be sent every 30 seconds while session is active.
   */
  startHeartbeat() {
    // Clear any existing intervals/timeouts
    this.clearHeartbeat();
    
    // Reset heartbeat counter and timing
    this.heartbeatCount = 0;
    this.lastHeartbeatTime = Date.now();

    // CRITICAL: Start the heartbeat interval - DO NOT skip this!
    this.heartbeatInterval = setInterval(() => {
      this.updateDurationTracking();
      this.sendHeartbeat();
    }, HEARTBEAT_INTERVAL_MS);

    // Send first heartbeat after 3 seconds to confirm system is working
    this.firstHeartbeatTimeout = setTimeout(() => {
      if (this.sessionId && this.sessionStartTime) {
        this.updateDurationTracking();
        this.sendHeartbeat();
      }
    }, 3000);
  }

  /**
   * Update duration tracking based on activity state
   * Called before each heartbeat to accumulate active/total time
   */
  updateDurationTracking() {
    const now = Date.now();
    if (!this.lastHeartbeatTime) {
      this.lastHeartbeatTime = now;
      return;
    }
    const elapsedMs = Math.max(0, now - this.lastHeartbeatTime);
    
    // Always increment total duration
    this.totalDurationMs += elapsedMs;
    
    // Only increment active duration when active
    if (this.isActive) {
      this.activeDurationMs += elapsedMs;
    }
    
    this.lastHeartbeatTime = now;
  }

  /**
   * Clear all heartbeat timers
   */
  clearHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
    if (this.firstHeartbeatTimeout) {
      clearTimeout(this.firstHeartbeatTimeout);
      this.firstHeartbeatTimeout = null;
    }
  }

  /**
   * Send a heartbeat to the server
   * CRITICAL: This deducts credits - durationSeconds must be a positive number!
   */
  async sendHeartbeat() {
    if (!this.sessionId || !this.sessionStartTime) {
      return;
    }

    this.heartbeatCount++;
    // Capture the session this heartbeat belongs to. A response (or error)
    // can arrive after the session was already ended/replaced — e.g. during
    // a mid-call voice change, which ends the session and starts a new one.
    // Acting on a stale response would tear down the NEW call.
    const requestSessionId = this.sessionId;

    try {
      const response = await voiceApi.sendHeartbeat(requestSessionId, this.getActiveDurationSeconds(), {
        totalSessionSeconds: this.getTotalDurationSeconds(),
        isActive: this.isActive,
      });

      if (this.sessionId !== requestSessionId) {
        devLog('[Voice] Ignoring stale heartbeat response for ended session', requestSessionId);
        return;
      }

      this.heartbeatFailureCount = 0;

      // Update store with credit info
      const { updateCredits } = useVoiceModeStore.getState().actions;
      updateCredits(response.credits, response.warnings);

      // CRITICAL: Handle warnings from server. The inactivity timeout ends
      // the call too, but must not show the "out of credits" modal.
      if (response.warnings?.shouldEnd) {
        const isInactivityTimeout = response.warnings?.reason === 'INACTIVE_TIMEOUT';
        await this._teardownSession({ showCreditsModal: !isInactivityTimeout });
      }

    } catch (error) {
      // A stale failure for a session we already left behind is not relevant.
      if (this.sessionId !== requestSessionId) {
        devLog('[Voice] Ignoring stale heartbeat failure for ended session', requestSessionId);
        return;
      }

      console.error('[Voice] Heartbeat failed:', error);

      // If the backend says the session no longer exists (ended elsewhere,
      // or reaped by the stale-session worker), don't keep an unbilled
      // OpenAI connection alive — end the call now. Same after several
      // consecutive failures of any kind: with billing unreachable for
      // ~2 minutes, the realtime connection is almost certainly dead too.
      const MAX_CONSECUTIVE_HEARTBEAT_FAILURES = 4;
      this.heartbeatFailureCount++;
      const sessionGone = error?.status === 404;
      const tooManyFailures = this.heartbeatFailureCount >= MAX_CONSECUTIVE_HEARTBEAT_FAILURES;

      if (sessionGone || tooManyFailures) {
        devWarn('[Voice] Ending call after heartbeat failure:', {
          sessionGone,
          consecutiveFailures: this.heartbeatFailureCount,
        });
        try {
          await this._teardownSession({ showCreditsModal: false });
        } catch (teardownErr) {
          console.error('[Voice] Teardown after heartbeat failure failed:', teardownErr);
        }
      }
      // Otherwise: transient failure, retry next interval.
    }
  }

  /**
   * Tear down the live call from outside the UI: stop capture, flush
   * transcript saves, disconnect the realtime transport, end the backend
   * session, and close the overlay. Used when the server tells us to end
   * (credits depleted) and when heartbeats fail hard.
   */
  async _teardownSession({ showCreditsModal = false } = {}) {
    const { stopVoiceMode } = useVoiceModeStore.getState().actions;

    // Stop local capture before closing the overlay so the browser releases
    // the microphone even though this shutdown was triggered outside the UI.
    try {
      voiceSocketManager.stopAudioCapture();
    } catch (cleanupErr) {
      devWarn('[Voice] Failed to stop audio capture during teardown:', cleanupErr?.message || cleanupErr);
    }

    try {
      await voiceSocketManager.flushPendingTranscriptSaves();
    } catch (cleanupErr) {
      devWarn('[Voice] Failed to flush transcript saves during teardown:', cleanupErr?.message || cleanupErr);
    }

    try {
      voiceSocketManager.disconnect();
    } catch (cleanupErr) {
      devWarn('[Voice] Failed to disconnect socket during teardown:', cleanupErr?.message || cleanupErr);
    }

    // End the backend session (this also refreshes user credits).
    // Do this after disconnect so the user cannot keep talking while
    // the final accounting request is in flight.
    await this.endSession();
    // Close the voice mode overlay completely
    stopVoiceMode();

    if (showCreditsModal) {
      setTimeout(() => {
        const { openModal } = useModalStore.getState().actions;
        openModal('VoiceCreditsDepletedModal');
      }, 100);
    }
  }

  /**
   * Synchronously detach the current session from this singleton: stop
   * heartbeat timers, take a final duration reading, and reset state so a
   * new call can start immediately. Returns a snapshot to pass to
   * finalizeSessionEnd(), or null if there was no active session.
   *
   * Splitting capture from the async backend call means a deferred
   * finalize can never tear down a NEWER session that started in the
   * meantime — it only ever acts on the captured snapshot.
   */
  captureSessionEnd() {
    // CRITICAL: Clear heartbeat timers first
    this.clearHeartbeat();

    if (!this.sessionId) {
      return null;
    }

    // Update duration tracking one final time
    this.updateDurationTracking();

    const snapshot = {
      sessionId: this.sessionId,
      activeDurationSeconds: this.getActiveDurationSeconds(),
      totalSessionSeconds: this.getTotalDurationSeconds(),
      isActive: this.isActive,
    };

    // Clear session state now so a new call can start cleanly.
    this.resetState();

    return snapshot;
  }

  /**
   * Complete the backend accounting (end-session REST call + credit
   * refresh) for a session previously captured via captureSessionEnd().
   * Safe to run in the background after the overlay has closed.
   */
  async finalizeSessionEnd(snapshot) {
    if (!snapshot?.sessionId) {
      return;
    }

    // Realtime WebRTC sessions on web/iOS/Android are created via REST, so
    // every platform should end through the REST endpoint. The old iOS raw
    // WebSocket path used to skip this because socket close handled cleanup.
    try {
      const response = await voiceApi.endSession(snapshot.sessionId, snapshot.activeDurationSeconds, {
        totalSessionSeconds: snapshot.totalSessionSeconds,
        isActive: snapshot.isActive,
      });

      // Update voice mode store with final credit info
      const { updateCredits } = useVoiceModeStore.getState().actions;
      if (response.credits) {
        updateCredits(response.credits, {});
      }

      // IMPORTANT: Refresh the user's credits in auth store
      try {
        await useAuthStore.getState().fetchUserCredits();
      } catch (creditRefreshError) {
        console.error('[Voice] Failed to refresh user credits:', creditRefreshError);
      }
    } catch (error) {
      console.error('[Voice] Failed to end session:', error);
    }
  }

  /**
   * End the voice session
   * CRITICAL: Must send final duration for accurate credit calculation
   */
  async endSession() {
    const snapshot = this.captureSessionEnd();
    await this.finalizeSessionEnd(snapshot);
  }

  /**
   * Called by voiceSocket.js when the WebSocket 'ready' event arrives (iOS only).
   * On iOS, the session is created by the WebSocket (not REST), so this
   * method sets up session tracking and starts heartbeats with the
   * sessionId returned by the server.
   *
   * @param {object} data - The 'ready' event data: { sessionId, conversationId, voiceId, state }
   */
  onWebSocketSessionReady(data) {
    const { sessionId, conversationId, voiceId } = data || {};
    
    console.log('[Voice] WebSocket session ready (iOS):', {
      sessionId,
      conversationId,
      voiceId,
    });

    if (!sessionId) {
      console.error('[Voice] WebSocket ready event missing sessionId');
      return;
    }

    // Set session state
    this.sessionId = sessionId;
    this.sessionStartTime = Date.now();
    
    // Store voice info if provided
    if (voiceId) {
      this.voiceId = voiceId;
    }
    
    // Initialize activity tracking
    this.activeDurationMs = 0;
    this.totalDurationMs = 0;
    this.isActive = true;
    this.lastHeartbeatTime = Date.now();
    this.heartbeatFailureCount = 0;
    
    // Update store with conversationId
    if (conversationId) {
      const { setConversationId } = useVoiceModeStore.getState().actions;
      setConversationId(conversationId);
    }

    // CRITICAL: Start heartbeats for credit tracking
    this.startHeartbeat();
    
    devLog('[Voice] iOS WebSocket session tracking initialized, heartbeats started');
  }

  /**
   * Reset all service state
   */
  resetState() {
    this.sessionId = null;
    this.sessionStartTime = null;
    this.heartbeatCount = 0;
    this.heartbeatFailureCount = 0;
    this.activeDurationMs = 0;
    this.totalDurationMs = 0;
    this.isActive = true;
    this.lastHeartbeatTime = null;
    this.voiceId = null;
    this.provider = null;
    this.audioConfig = null;
    this.vadConfig = null;
  }

  /**
   * Resume a session after reconnection
   * Called when the socket successfully resumes a session
   * @param {object} resumeData - Data from the resume_success event
   */
  resumeSession(resumeData) {
    const { sessionId, conversationId, disconnectDuration } = resumeData;
    
    devLog('[Voice] Resuming session:', sessionId, 'after', disconnectDuration, 'ms disconnect');
    
    // Restore session ID if it was cleared
    if (sessionId && !this.sessionId) {
      this.sessionId = sessionId;
    }
    
    // Ensure heartbeat is running
    if (!this.heartbeatInterval && this.sessionId) {
      devLog('[Voice] Restarting heartbeat after resume');
      this.startHeartbeat();
    }
    
    // Resume activity tracking
    this.isActive = true;
    this.lastHeartbeatTime = Date.now();
    
    // Update store with conversation ID if needed
    if (conversationId) {
      const { setConversationId } = useVoiceModeStore.getState().actions;
      setConversationId(conversationId);
    }
    
    devLog('[Voice] Session resumed successfully');
  }

  /**
   * Handle session resume failure
   * Called when the socket fails to resume a session
   * @param {object} failData - Data from the resume_failed event
   */
  async handleResumeFailed(failData) {
    devLog('[Voice] Session resume failed:', failData.reason, failData.message);
    
    // Clear heartbeat timers first
    this.clearHeartbeat();
    
    // If we had a valid session ID, try to end it on the backend to prevent orphaned sessions.
    // On iOS, the WebSocket close handles cleanup — skip the REST call.
    const sessionIdToEnd = this.sessionId;
    if (sessionIdToEnd && failData.reason !== 'session_expired' && failData.reason !== 'session_not_found') {
      if (Platform.OS === 'ios') {
        devLog('[Voice] iOS: Skipping REST cleanup for orphaned session (WebSocket handles it):', sessionIdToEnd);
      } else {
        try {
          devLog('[Voice] Cleaning up orphaned backend session:', sessionIdToEnd);
          await voiceApi.endSession(sessionIdToEnd, this.getActiveDurationSeconds(), {
            totalSessionSeconds: this.getTotalDurationSeconds(),
            isActive: false,
            isDisconnected: true,
          });
        } catch (endErr) {
          // Log but don't throw - session may already be ended on backend
          devWarn('[Voice] Failed to end orphaned session:', endErr.message);
        }
      }
    }
    
    // Reset local state
    this.resetState();
  }

  /**
   * Pause activity tracking (e.g., when user mutes)
   * Snapshot the elapsed segment FIRST so the active time accrued since the
   * last heartbeat is credited as active — otherwise muting mid-interval
   * retroactively reclassifies the whole interval as inactive.
   */
  pauseActivityTracking() {
    this.updateDurationTracking();
    this.isActive = false;
    devLog('[Voice] Activity tracking paused');
  }

  /**
   * Resume activity tracking
   * Snapshot the muted segment first (counts toward total, not active),
   * then flip back to active.
   */
  resumeActivityTracking() {
    this.updateDurationTracking();
    this.isActive = true;
    devLog('[Voice] Activity tracking resumed');
  }

  /**
   * Check if a session is currently active
   */
  isSessionActive() {
    return !!this.sessionId && !!this.sessionStartTime;
  }
}

// Export singleton instance
const voiceModeService = new VoiceModeService();
export default voiceModeService;
